import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dropdown } from 'antd';
import type { MenuProps, DropdownProps } from 'antd';
import { LogoutOutlined, SettingOutlined } from '@ant-design/icons';
import { LuSun, LuMoon } from 'react-icons/lu';
import Avvvatar from 'avvvatars-react';
import { ThemeContext, SettingsContext } from '@/state/contexts';
import { useAuthUser } from '@/hooks/useAuthUser';
import { useLogout } from '@/state/hooks/useLogout';
import styles from './SidebarFooter.module.css';

function getInitials(firstName: string, lastName: string): string {
  const first = firstName.charAt(0).toUpperCase();
  const last = lastName.charAt(0).toUpperCase();
  return `${first}${last}`;
}

export function SidebarFooter() {
  const navigate = useNavigate();
  const { user } = useAuthUser();
  const { darkTheme, setDarkTheme } = useContext(ThemeContext);
  const { setSettings } = useContext(SettingsContext);
  const handleLogout = useLogout();

  const initials = getInitials(user.firstName, user.lastName);
  const userFullName = `${user.firstName} ${user.lastName}`.trim();

  const handleSettings = () => {
    navigate('/settings');
  };

  const handleToggleTheme = () => {
    const next = !darkTheme;
    setDarkTheme(next);
    setSettings({ darkMode: next });
  };

  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'settings') {
      handleSettings();
    } else if (key === 'logout') {
      handleLogout();
    }
  };

  const items: MenuProps['items'] = [
    {
      key: 'settings',
      label: 'Settings',
      icon: <SettingOutlined />,
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      label: 'Sign out',
      icon: <LogoutOutlined />,
      danger: true,
    },
  ];

  const dropdownProps: DropdownProps = {
    menu: { items, onClick: handleMenuClick },
    placement: 'topRight',
    trigger: ['click'],
  };

  return (
    <div className={styles.footer}>
      <button
        type="button"
        className={styles.themeButton}
        onClick={handleToggleTheme}
        aria-label={darkTheme ? 'Switch to light mode' : 'Switch to dark mode'}
        title={darkTheme ? 'Light mode' : 'Dark mode'}
      >
        {darkTheme ? (
          <LuSun className={styles.themeIcon} />
        ) : (
          <LuMoon className={styles.themeIcon} />
        )}
      </button>

      <Dropdown {...dropdownProps}>
        <div className={styles.userSection} title={userFullName}>
          {/* eslint-disable-next-line react/style-prop-object -- avvvatars-react uses 'style' as a variant prop, not CSS */}
          <Avvvatar value={initials} style="character" size={40} />
        </div>
      </Dropdown>
    </div>
  );
}
